/**
 * Shared schedule-ingestion UI constants + pure helpers.
 *
 * Extracted so the ScheduleIngestionModal and the Build Itinerary wizard's
 * Step 1 (which both drive the same create -> upload/start -> poll flow) share a
 * single source of truth. In particular URL_FRIENDLY_FALLBACK must stay
 * byte-for-byte identical to the backend's blocked-link message because
 * isBlockedLinkMessage keys off an exact match; keeping one copy here removes
 * the drift risk of two hand-maintained duplicates.
 */

import { MAX_FILE_SIZE } from '../../../../constants/extraction';
import type { ScheduleIngestionStatus } from '../../../../types/schedule-ingestion';

export const FRIENDLY_FALLBACK =
  'We could not read that file. Try a clearer PDF or screenshot of the schedule.';

// MUST stay byte-for-byte identical to the message the backend returns on a
// blocked/unreadable link. isBlockedLinkMessage does an exact-match on it.
export const URL_FRIENDLY_FALLBACK =
  "We couldn't read that link. Upload a PDF or screenshot instead.";

/** Map a raw ingestion status to a friendly progress label for the spinner. */
export function progressLabelForStatus(status: ScheduleIngestionStatus): string {
  switch (status) {
    case 'Fetching':
      return 'Reading the link...';
    default:
      return 'Reading the games from your schedule...';
  }
}

/**
 * Validate a picked schedule file before starting a (potentially long) upload +
 * extraction run. Returns a friendly error message, or null when the file is
 * acceptable. Only PDFs and images are supported, up to MAX_FILE_SIZE.
 */
export function validateScheduleFile(file: File): string | null {
  const isPdf =
    file.type === 'application/pdf' ||
    file.name.toLowerCase().endsWith('.pdf');
  const isImage = file.type.startsWith('image/');
  if (!isPdf && !isImage) {
    return 'That file type is not supported. Upload a PDF or an image.';
  }
  if (file.size > MAX_FILE_SIZE) {
    return 'That file is too large. Please upload a file under 50 MB.';
  }
  return null;
}
